import Fragment from 'ember-data-model-fragments/fragment';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr } from '@warp-drive/legacy/model';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import AttendanceViewMode from '@linc-technologies/linced-common/enums/person-option-attendance-view-mode';
import NotificationPreference from '@linc-technologies/linced-common/enums/person-option-notification-preference';
import SearchMode from '@linc-technologies/linced-common/enums/person-option-search-mode';

// Proto: people.PersonOptions.
export default class Options extends Fragment {
  declare [Type]: 'people/options';

  @attr('number', {
    defaultValue: NotificationPreference.DEFAULT,
  })
  declare notificationPreference: EnumValue<typeof NotificationPreference>;

  @attr('number', {
    defaultValue: SearchMode.GRID,
  })
  declare searchMode: EnumValue<typeof SearchMode>;

  @attr('number', {
    defaultValue: SearchMode.GRID,
  })
  declare myGroupsMode: EnumValue<typeof SearchMode>;

  @attr('number', {
    defaultValue: SearchMode.GRID,
  })
  declare sharedGroupsMode: EnumValue<typeof SearchMode>;

  @attr('number', {
    defaultValue: SearchMode.GRID,
  })
  declare autoGroupsMode: EnumValue<typeof SearchMode>;

  @attr('number', {
    defaultValue: SearchMode.GRID,
  })
  declare classGroupsMode: EnumValue<typeof SearchMode>;

  @attr('number', {
    defaultValue: SearchMode.GRID,
  })
  declare groupMode: EnumValue<typeof SearchMode>;

  @attr('number', {
    defaultValue: AttendanceViewMode.PERIOD,
  })
  declare attendanceViewMode: EnumValue<typeof AttendanceViewMode>;

  @array('string')
  declare enrolmentApplicationsCustomFields: StatefulArray<string>;

  // Add the dataCards property with correct capitalization
  @attr()
  declare dataCards: unknown;

  // defaultMilestoneSet has no default, as the default is calculated based on
  // the school's Milestone Sets.  It may be a UUID or the string "default".
  @attr('string')
  declare defaultMilestoneSet: string | undefined;

  // hideArchivedLineItems stores the person's line item display mode
  @attr('boolean', { defaultValue: false })
  declare hideArchivedLineItems: boolean;

  // autosaveNumericGrades stores the person's preference for autosaving
  // numeric grades in the group grading UI.
  @attr('boolean', { defaultValue: false })
  declare autosaveNumericGrades: boolean;

  // hasAcknowledgedReportDisclaimer stores whether the user has accepted the
  // disclaimer shown when entering the report writing route.
  @attr('boolean', { defaultValue: false })
  declare hasAcknowledgedReportDisclaimer: boolean;

  // progressOverviewMode, progressOverviewYear and
  // progressOverviewControlsMinimised store the person's settings on the
  // Milestones progress overview: how it reads the year, which year it shows
  // (empty for the current one), and whether its floating settings are
  // minimised. The mode is 0 when unset, which reads the year by half.
  @attr('number', { defaultValue: 0 })
  declare progressOverviewMode: number;

  @attr('string', { defaultValue: '' })
  declare progressOverviewYear: string;

  @attr('boolean', { defaultValue: false })
  declare progressOverviewControlsMinimised: boolean;
}
